import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Meta from '../components/Meta';
import './AboutUsModern.css';
import foto2 from '../assets/foto2.webp';
import { FaHandshake, FaLightbulb, FaRocket } from 'react-icons/fa'; // Assuming react-icons is installed

function AboutUsModern() {
    const { t } = useTranslation();
    const observerRef = useRef(null);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                    }
                });
            },
            { threshold: 0.1 }
        );

        const hiddenElements = document.querySelectorAll('.animate-on-scroll');
        hiddenElements.forEach((el) => observerRef.current.observe(el));

        return () => {
            if (observerRef.current) {
                hiddenElements.forEach((el) => observerRef.current.unobserve(el));
            }
        };
    }, []);

    const methodSteps = t('about_us_page.method_steps', { returnObjects: true });
    const valuesItems = t('about_us_page.values_items', { returnObjects: true });

    // Helper to get icon based on index or title (simple mapping for demo)
    const getStepIcon = (index) => {
        const icons = [<FaLightbulb />, <FaHandshake />, <FaRocket />];
        return icons[index % icons.length];
    };

    return (
        <div className="about-us-modern">
            <Meta title={t('about_us_page.meta_title')} description={t('about_us_page.meta_description')} />

            {/* Hero Section */}
            <section className="modern-hero">
                <div className="hero-content">
                    <h1>{t('about_us_page.hero_title')}</h1>
                    <p className="subtitle">{t('about_us_page.hero_subtitle')}</p>
                </div>
            </section>

            {/* History Section */}
            <section className="modern-section">
                <div className="history-container">
                    <div className="history-text animate-on-scroll">
                        <h2>{t('about_us_page.history_title')}</h2>
                        <p>{t('about_us_page.history_text_p1')}</p>
                        <p>{t('about_us_page.history_text_p2')}</p>
                    </div>
                    <div className="history-image animate-on-scroll delay-200">
                        <img src={foto2} alt="Our History" />
                    </div>
                </div>
            </section>

            {/* Method Section */}
            <section className="modern-section bg-light">
                <div className="animate-on-scroll">
                    <h2>{t('about_us_page.method_title')}</h2>
                    <p style={{ marginBottom: '3rem', fontSize: '1.2rem' }}>
                        {t('about_us_page.method_subtitle')}
                    </p>
                </div>

                <div className="steps-grid">
                    {methodSteps.map((step, index) => (
                        <div key={index} className={`step-card animate-on-scroll delay-${(index + 1) * 100}`}>
                            <div className="step-number">{index + 1}</div>
                            <h3>{step.title}</h3>
                            <p>{step.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Values Section */}
            <section className="modern-section">
                <h2 className="animate-on-scroll">{t('about_us_page.values_title')}</h2>
                <div className="values-grid">
                    {valuesItems.map((value, index) => (
                        <div key={index} className={`value-card animate-on-scroll delay-${(index + 1) * 100}`}>
                            <div className="value-icon-wrapper">
                                {getStepIcon(index)}
                            </div>
                            <h3>{value.title}</h3>
                            <p>{value.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Promise Section */}
            <section className="promise-section">
                <div className="promise-content animate-on-scroll">
                    <h2>{t('about_us_page.promise_title')}</h2>
                    <p className="promise-text">"{t('about_us_page.promise_text')}"</p>
                </div>
            </section>
        </div>
    );
}

export default AboutUsModern;
